import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, getToken } from "../../lib/api";
import { C } from "./AccountingLayout";
import { toast } from "sonner";
import { Upload, RefreshCw, CheckCircle2, AlertTriangle, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";

const money = (n) => `₦${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettlementReconciliation() {
  const [uploads, setUploads] = useState([]);
  const [detail, setDetail] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const fileRef = useRef();
  const [form, setForm] = useState({ account_code: "1050", channel: "transfer_settlement", location_id: "" });
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/accounting/settlements").then(setUploads).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); api.get("/locations").then(setLocations).catch(() => {}); }, []);

  const doUpload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Pick a CSV file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("account_code", form.account_code);
      fd.append("channel", form.channel);
      if (form.location_id) fd.append("location_id", form.location_id);
      const r = await fetch(`${BASE}/accounting/settlements/upload`, {
        method: "POST", body: fd,
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!r.ok) throw new Error(await r.text());
      const j = await r.json();
      toast.success(`Upload done — matched ${j.matched}, exceptions ${j.exceptions}`);
      setUploadOpen(false); load();
      const full = await api.get(`/accounting/settlements/${j.id}`); setDetail(full);
    } catch (e) { toast.error(String(e.message || e)); } finally { setBusy(false); }
  };

  const rematch = async (uid) => {
    try { const r = await api.post(`/accounting/settlements/${uid}/rematch`);
      toast.success(`Re-matched: ${r.matched} ok · ${r.exceptions} exceptions`); load(); if (detail?.id === uid) setDetail(await api.get(`/accounting/settlements/${uid}`));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resolveExc = async (exc) => {
    const reason = window.prompt("Resolution note:");
    if (!reason) return;
    try {
      await api.post(`/accounting/reconciliation-exceptions/${exc.id}/resolve`, { resolution: reason, action: "mark_reconciled" });
      toast.success("Resolved");
      if (detail) setDetail(await api.get(`/accounting/settlements/${detail.id}`));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="settlement-recon">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-widest uppercase font-semibold" style={{ color: C.softMuted }}>Bank / Settlement</div>
          <div className="font-brand text-lg font-bold" style={{ color: C.text }}>Statement Reconciliation</div>
        </div>
        <button onClick={() => setUploadOpen(true)} className="h-10 px-4 rounded-xl text-sm font-semibold text-white flex items-center gap-2" style={{ background: C.burgundy }} data-testid="upload-btn"><Upload size={15} /> Upload Statement</button>
      </div>

      <div className="rounded-2xl border overflow-x-auto" style={{ background: C.panel, borderColor: C.border }}>
        <table className="w-full text-sm">
          <thead><tr style={{ color: C.softMuted, background: C.borderSoft }}>
            {["File", "Channel", "Rows", "Matched", "Exceptions", "Uploaded", ""].map((h) => <th key={h} className="text-left py-2.5 px-3 text-[10px] tracking-widest">{h}</th>)}
          </tr></thead>
          <tbody>
            {uploads.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-xs" style={{ color: C.softMuted }}>No statements uploaded yet.</td></tr>}
            {uploads.map((u) => (
              <tr key={u.id} className="border-t" style={{ borderColor: C.borderSoft, color: C.text }}>
                <td className="py-2 px-3 font-semibold flex items-center gap-2"><FileText size={14} style={{ color: C.burgundy }} />{u.filename}{u.parse_ocr && <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: C.amberTint, color: C.amber }}>OCR</span>}</td>
                <td className="py-2 px-3">{u.channel}</td>
                <td className="py-2 px-3">{u.row_count}</td>
                <td className="py-2 px-3" style={{ color: C.green }}>{u.matched}</td>
                <td className="py-2 px-3" style={{ color: u.exceptions ? C.red : C.text }}>{u.exceptions}</td>
                <td className="py-2 px-3 text-xs" style={{ color: C.softMuted }}>{(u.uploaded_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="py-2 px-3 flex gap-2">
                  <button className="text-xs px-2 py-1 rounded-lg" style={{ border: `1px solid ${C.border}`, color: C.burgundy }} onClick={async () => setDetail(await api.get(`/accounting/settlements/${u.id}`))} data-testid={`view-${u.id}`}>View</button>
                  <button className="text-xs px-2 py-1 rounded-lg" style={{ border: `1px solid ${C.border}`, color: C.text }} onClick={() => rematch(u.id)}><RefreshCw size={11} className="inline mr-1" />Re-match</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={uploadOpen} onOpenChange={(o) => !o && setUploadOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Bank / Settlement Statement</DialogTitle>
            <DialogDescription>CSV or PDF (text-based or scanned — OCR runs automatically). Auto-match runs on upload.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><label className="text-[10px] tracking-widest uppercase font-semibold" style={{ color: C.softMuted }}>File</label>
              <input ref={fileRef} type="file" accept=".csv,.pdf,.txt" className="mt-1 w-full text-sm" data-testid="upload-file" /></div>
            <div><label className="text-[10px] tracking-widest uppercase font-semibold" style={{ color: C.softMuted }}>Channel</label>
              <select value={form.channel} onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))} className="w-full h-10 rounded-lg px-3 text-sm" style={{ border: `1px solid ${C.border}` }}>
                <option value="transfer_settlement">Transfer Settlement</option>
                <option value="card_settlement">Card Settlement</option>
                <option value="bank">Bank (any)</option>
              </select></div>
            <div><label className="text-[10px] tracking-widest uppercase font-semibold" style={{ color: C.softMuted }}>Account</label>
              <input value={form.account_code} onChange={(e) => setForm((f) => ({ ...f, account_code: e.target.value }))} className="w-full h-10 rounded-lg px-3 text-sm" style={{ border: `1px solid ${C.border}` }} data-testid="upload-account" /></div>
            <div><label className="text-[10px] tracking-widest uppercase font-semibold" style={{ color: C.softMuted }}>Location (optional)</label>
              <select value={form.location_id} onChange={(e) => setForm((f) => ({ ...f, location_id: e.target.value }))} className="w-full h-10 rounded-lg px-3 text-sm" style={{ border: `1px solid ${C.border}` }}>
                <option value="">All</option>{locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select></div>
          </div>
          <DialogFooter>
            <button onClick={() => setUploadOpen(false)} className="h-10 px-4 rounded-xl text-sm" style={{ color: C.muted }}>Cancel</button>
            <button onClick={doUpload} disabled={busy} className="h-10 px-4 rounded-xl text-sm font-semibold text-white" style={{ background: C.burgundy }} data-testid="upload-run">{busy ? "Uploading…" : "Upload & Match"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {detail && (
        <Dialog open onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{detail.filename} · {detail.channel}</DialogTitle>
              <DialogDescription>{detail.row_count} rows · {detail.matched} matched · {detail.exceptions} exceptions</DialogDescription>
            </DialogHeader>
            {detail.exceptions?.length > 0 && (
              <div className="rounded-xl border p-3 mb-3" style={{ background: C.redTint, borderColor: C.red }}>
                <div className="flex items-center gap-2 text-sm font-semibold mb-2" style={{ color: C.red }}><AlertTriangle size={14} /> Open Exceptions</div>
                <div className="space-y-1">
                  {detail.exceptions.map((e) => (
                    <div key={e.id} className="flex items-center justify-between text-xs">
                      <span>{e.kind} · bank {money(e.amount_bank)} · system {money(e.amount_system)} · diff {money(e.difference)}</span>
                      <button onClick={() => resolveExc(e)} className="text-xs px-2 py-0.5 rounded" style={{ background: "#fff", border: `1px solid ${C.red}`, color: C.red }} data-testid={`resolve-${e.id}`}>Resolve</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="overflow-x-auto"><table className="w-full text-xs">
              <thead><tr style={{ color: C.softMuted, background: C.borderSoft }}>{["Date", "Reference", "Description", "Amount", "Status", "Diff"].map((h) => <th key={h} className="text-left py-2 px-2 text-[10px] tracking-widest">{h}</th>)}</tr></thead>
              <tbody>
                {(detail.rows || []).map((r) => (
                  <tr key={r.id} className="border-t" style={{ borderColor: C.borderSoft, color: C.text }}>
                    <td className="py-2 px-2">{r.date}</td>
                    <td className="py-2 px-2 font-mono">{r.reference}</td>
                    <td className="py-2 px-2">{r.description}</td>
                    <td className="py-2 px-2 font-semibold">{money(r.amount)}</td>
                    <td className="py-2 px-2"><span style={{ color: r.match_status === "matched" ? C.green : r.match_status === "manually_reconciled" ? C.gold : C.red }}>{r.match_status}</span></td>
                    <td className="py-2 px-2">{r.difference ? money(r.difference) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
            <DialogFooter>
              <button onClick={() => rematch(detail.id)} className="h-9 px-3 rounded-xl text-xs" style={{ border: `1px solid ${C.border}`, color: C.text }}><RefreshCw size={12} className="inline mr-1" /> Re-match</button>
              <button onClick={() => setDetail(null)} className="h-9 px-3 rounded-xl text-xs text-white" style={{ background: C.burgundy }}>Close</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
